/* External */
import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

/* Constants */
import { PRIVACY_POLICY_PAGE, TERMS_AND_CONDITIONS_PAGE } from '../../../../components/TermsOfService/constants';

/* Components */
import FocusableButton from '../../../../components/FocusableButton/FocusableButton';

/* Services */

/* Utils */
import { Direction } from '../../../../utils/spatialNavigation';
import { getLink, Routes } from '../../../../utils/router';
import { noOp } from '../../../../utils/functions';
import { redirectTo } from '../../../../utils/history';
import { SETTINGS } from '../../../../utils/focusKey';
import { GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

function handleArrowPressNoOp(noOpDirections) {
  return (direction) => noOpDirections.every(
    (noOpDirection) => direction !== noOpDirection,
  );
}

function redirectToAgreement(page) {
  return () => redirectTo(
    getLink(Routes.Private.TermsOfService, {
      page,
    }),
  );
}

function getCtaProps(agreementType) {
  return {
    privacyPolicy: {
      agreementPage: PRIVACY_POLICY_PAGE,
      focusKey: SETTINGS.OPEN_PRIVACY_POLICY,
      label: 'ppLabel',
      noOpDirections: [Direction.RIGHT, Direction.DOWN],
    },
    termsAndConditions: {
      agreementPage: TERMS_AND_CONDITIONS_PAGE,
      focusKey: SETTINGS.OPEN_TERMS_AND_CONDITIONS,
      label: 'tcLabel',
      noOpDirections: [Direction.UP, Direction.RIGHT],
    },
  }[agreementType];
}

function OpenAgreementCta({ onArrowPress, type, settingsCommonTagEvent }) {
  const {
    agreementPage, focusKey, label, noOpDirections,
  } = getCtaProps(type);

  const handleNoOpDirections = handleArrowPressNoOp(noOpDirections);
  const handleArrowPress = (direction) => (
    handleNoOpDirections(direction) && onArrowPress(direction)(focusKey)
  );

  const handleRedirectToAgreement = redirectToAgreement(agreementPage);

  const handleTaggingEvents = useCallback(() => {
    if (agreementPage === 'termsAndConditions') {
      settingsCommonTagEvent({
        subCategory: GTM_SUBCATEGORIES.SETTINGS.SETTINGS,
        eventName: GTM_EVENT_NAMES.SETTINGS.TERMS_AND_CONDITIONS,
      });
    } else if (agreementPage === 'privacyPolicy') {
      settingsCommonTagEvent({
        subCategory: GTM_SUBCATEGORIES.SETTINGS.SETTINGS,
        eventName: GTM_EVENT_NAMES.SETTINGS.PRIVACY_POLICY,
      });
    }
  }, [agreementPage, settingsCommonTagEvent]);

  const handleEnterPress = useCallback(() => {
    handleRedirectToAgreement();
    handleTaggingEvents();
  }, [handleRedirectToAgreement, handleTaggingEvents]);

  return (
    <FocusableButton
      primary
      focusKey={focusKey}
      className="dtv-html5-c-setting__button"
      onEnterPress={handleEnterPress}
      onArrowPress={handleArrowPress}
    >
      <FormattedMessage id={`settings.legal.${label}`} />
    </FocusableButton>
  );
}

OpenAgreementCta.defaultProps = {
  onArrowPress: noOp,
  settingsCommonTagEvent: noOp,
};

OpenAgreementCta.propTypes = {
  onArrowPress: PropTypes.func,
  type: PropTypes.string.isRequired,
  settingsCommonTagEvent: PropTypes.func,
};

export default memo(OpenAgreementCta);
